import type { ColocaProgressSegment } from "@/shared/components/feedback/ColocaProgress";

import {
  CANCELLABLE_BATCH_STATUSES,
  EXECUTION_BATCH_STATUSES,
  LIVE_BATCH_STATUSES,
  REVIEW_BATCH_STATUSES,
  TERMINAL_BATCH_STATUSES,
} from "../constants/payoutBatches.constants";
import {
  type PayoutBatch,
  type PayoutBatchCounters,
  type PayoutBatchItem,
  PayoutBatchItemStatus,
  PayoutBatchStatus,
} from "../types/payoutBatches.types";

export const isLiveStatus = (status: PayoutBatchStatus): boolean => LIVE_BATCH_STATUSES.has(status);

export const isTerminalStatus = (status: PayoutBatchStatus): boolean => TERMINAL_BATCH_STATUSES.has(status);

export const isExecutionStatus = (status: PayoutBatchStatus): boolean => EXECUTION_BATCH_STATUSES.has(status);

export const canCancel = (status: PayoutBatchStatus): boolean => CANCELLABLE_BATCH_STATUSES.has(status);

export const isReviewStatus = (status: PayoutBatchStatus): boolean => REVIEW_BATCH_STATUSES.has(status);

/** Rows the run loop still has to take to the provider — what a resume would pick up. */
export const runnableRows = (counters: PayoutBatchCounters): number =>
  counters.selected + counters.queued + counters.processing;

/**
 * `POST /:id/resume` is accepted on a FAILED batch, or on a completed one that a premature finish left with
 * rows never processed (`PayoutBatchRetryService.resume`).
 */
export const canResume = (batch: Pick<PayoutBatch, "status" | "counters">): boolean =>
  batch.status === PayoutBatchStatus.FAILED ||
  ((batch.status === PayoutBatchStatus.COMPLETED ||
    batch.status === PayoutBatchStatus.COMPLETED_WITH_ERRORS) &&
    runnableRows(batch.counters) > 0);

export const canRetryItem = (item: Pick<PayoutBatchItem, "status">): boolean =>
  item.status === PayoutBatchItemStatus.FAILED;

/** Rows that were chosen for execution, whatever became of them. */
export const chosenRows = (counters: PayoutBatchCounters): number =>
  counters.selected +
  counters.queued +
  counters.processing +
  counters.submitted +
  counters.paid +
  counters.failed +
  counters.reversed +
  counters.cancelled;

/** Rows the run has taken to the provider, one way or another. */
export const processedRows = (counters: PayoutBatchCounters): number =>
  counters.submitted + counters.paid + counters.failed + counters.reversed + counters.cancelled;

export interface ExecutionProgress {
  segments: ColocaProgressSegment[];
  /** Denominator of the strip: the chosen rows. */
  max: number;
  /** Accessible position: rows that reached the provider. */
  done: number;
}

/**
 * The batch strip: paid, failed (and reversed), submitted-awaiting-settlement, processing — stacked over the
 * chosen rows; what is left of the track is the pending remainder. Labels are the caller's to translate, so
 * the segments carry keys and the caller maps them.
 */
export const executionProgress = (counters: PayoutBatchCounters): ExecutionProgress => {
  const max = chosenRows(counters);
  const candidates: ColocaProgressSegment[] = [
    { key: "paid", value: counters.paid, tone: "success" },
    {
      key: "failed",
      value: counters.failed + counters.reversed,
      tone: "danger",
    },
    { key: "submitted", value: counters.submitted, tone: "warning" },
    { key: "processing", value: counters.processing, tone: "primary" },
  ];
  const segments = candidates.filter((segment) => segment.value > 0);
  return { segments, max: Math.max(max, 1), done: processedRows(counters) };
};

/** Percentage of the chosen rows that reached the provider; 0 when nothing was chosen. */
export const executionPercent = (counters: PayoutBatchCounters): number => {
  const max = chosenRows(counters);
  return max === 0 ? 0 : Math.round((processedRows(counters) / max) * 100);
};
